/**
 * Fait : dit à la personne, en tête de l'écran de l'argent, si sa prochaine
 *   demande partira — et de combien elle dispose réellement. Le chiffre affiché
 *   est celui des DEUX plafonds croisés, pas celui du sien.
 *
 *   CE COMPOSANT EXISTE À CAUSE D'UN DÉFAUT RÉEL, trouvé par une épreuve
 *   indépendante le 2026-08-25. L'écran annonçait « Mon plafond : 50,00 €. Il
 *   me reste 50,00 € » alors que le plafond de la famille valait zéro, donc que
 *   rien ne pouvait partir. La règle générale — « c'est toujours le plus bas
 *   des deux qui arrête » — était bien écrite juste en dessous, en texte
 *   atténué. Mais une règle générale n'est pas un état présent, et à fort
 *   grossissement on lit le chiffre sans la nuance. C'est un chiffre faux qui
 *   ne se dénonce pas : la personne aurait cherché la panne ailleurs.
 *
 *   QUAND C'EST FERMÉ, LA CAUSE EST NOMMÉE, et l'annonce est urgente. Dire
 *   « rien ne peut partir » sans dire d'où vient le blocage envoie la personne
 *   chercher dans le mauvais écran — et, pour un membre ordinaire, l'écran qui
 *   débloque n'est pas le sien.
 *
 * Ne fait pas : il n'appelle rien, ne règle aucun plafond, et ne dit pas si
 *   telle question précise passera — il ne connaît pas son coût.
 */

import { Annonce } from '../gabarits/Annonce.tsx';
import type { BudgetsVus } from './api-argent.ts';
import { ceQuiReste } from './ce-qui-reste.ts';
import { enEuros } from './montants.ts';

/** Pourquoi rien ne peut partir, dit à la personne qui le lit. */
const POURQUOI_C_EST_FERME: Readonly<Record<'famille' | 'moi' | 'les-deux', string>> = {
  famille:
    'Rien ne peut partir pour l’instant : le plafond de toute la famille est à zéro. Même les ' +
    'modèles compris dans un abonnement, qui ne coûtent rien, sont refusés. Seul le parent ' +
    'administrateur peut relever ce plafond.',
  moi:
    'Rien ne peut partir pour l’instant : aucun plafond personnel ne vous a été ouvert, ou il ' +
    'est à zéro. Demandez au parent administrateur de vous en poser un.',
  'les-deux':
    'Rien ne peut partir pour l’instant : ni le plafond de la famille ni le vôtre n’est ouvert. ' +
    'Les deux doivent l’être, et seul le parent administrateur les pose.',
};

export function EtatDeMonBudget({ budgets }: { readonly budgets: BudgetsVus }) {
  const reste = ceQuiReste(budgets.global, budgets.membre);

  return (
    <>
      {!reste.ouvert && (
        <Annonce urgence="urgent" nature="refus" data-role="rien-ne-peut-partir">
          {POURQUOI_C_EST_FERME[reste.cause]}
        </Annonce>
      )}

      {/*
       * TOUT DANS UNE SEULE PHRASE, l'état d'abord, les chiffres ensuite. La
       * première version séparait « ce qui reste » et « le détail » en deux
       * paragraphes : on pouvait alors lire le chiffre sans l'état, ce qui est
       * exactement le défaut qu'on répare. Et une personne qui parcourt
       * l'écran à la voix ou à fort grossissement lit rarement le paragraphe
       * suivant.
       */}
      <p data-role="mon-plafond">
        {reste.ouvert ? '' : 'Rien ne peut partir en ce moment : le plafond nécessaire est fermé. '}
        Votre plafond personnel : {enEuros(budgets.membre.plafondCentimes)}. Déjà dépensé :{' '}
        {enEuros(budgets.membre.consommeCentimes)}. Retenu par des demandes en cours :{' '}
        {enEuros(budgets.membre.engageCentimes)}.
        {reste.ouvert
          ? ` Il vous reste ${enEuros(reste.resteCentimes)} à dépenser` +
            (reste.borne === 'famille'
              ? ' — c’est ce que la famille entière peut encore dépenser, et c’est cela qui vous ' +
                'limite en premier.'
              : '.')
          : ''}
        {budgets.membre.pose
          ? ''
          : ' Aucun plafond personnel ne vous a jamais été posé — et un plafond absent vaut zéro.'}
      </p>

      <p data-role="plafond-de-la-famille" className="texte-attenue">
        {budgets.global.pose
          ? `Plafond de toute la famille : ${enEuros(budgets.global.plafondCentimes)}, dont ` +
            `${enEuros(budgets.global.consommeCentimes)} déjà dépensés.`
          : 'Aucun plafond n’a jamais été posé pour la famille — et un plafond absent vaut zéro.'}
      </p>
    </>
  );
}
